import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  Handle,
  Position,
  applyEdgeChanges,
  applyNodeChanges,
  addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

const TYPE_STYLES = {
  trigger: { badge: 'bg-violet-500/15 text-violet-300 border-violet-500/30', border: 'border-l-violet-400' },
  agent: { badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30', border: 'border-l-amber-400' },
  agent_tech: { badge: 'bg-orange-500/15 text-orange-300 border-orange-500/30', border: 'border-l-orange-400' },
  action: { badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30', border: 'border-l-sky-400' },
};

const FlowNode = ({ data }) => {
  const style = TYPE_STYLES[data.nodeType] || TYPE_STYLES.action;

  return (
    <div className={`min-w-[220px] rounded-2xl border border-border-subtle bg-background/85 backdrop-blur-md border-l-4 ${style.border} px-4 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.15)]`} data-testid={`agent-os-node-${data.id}`}>
      <Handle type="target" position={Position.Left} className="!bg-primary !border-0 !w-3 !h-3" />
      <div className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${style.badge}`}>{data.nodeType.replace('_', ' ')}</div>
      <p className="mt-3 text-sm font-semibold text-text-heading">{data.label}</p>
      <p className="mt-2 text-xs leading-relaxed text-text-muted">{data.description || 'Nodo operativo del workflow'}</p>
      <Handle type="source" position={Position.Right} className="!bg-primary !border-0 !w-3 !h-3" />
    </div>
  );
};

const nodeTypes = {
  trigger: FlowNode,
  agent: FlowNode,
  agent_tech: FlowNode,
  action: FlowNode,
};

const toFlowNode = (node) => ({
  id: node.id,
  type: node.type,
  position: node.position || { x: 0, y: 0 },
  data: {
    id: node.id,
    label: node.label,
    description: node.description,
    nodeType: node.type,
  },
});

const serializeNodes = (nodes, workflowNodes) => nodes.map((node) => {
  const original = workflowNodes.find((item) => item.id === node.id) || {};
  return {
    ...original,
    id: node.id,
    type: node.type,
    position: node.position,
  };
});

export default function WorkflowCanvas({ workflow, selectedNodeId, onSelectNode, onWorkflowChange }) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const isHydratingRef = useRef(false);

  useEffect(() => {
    isHydratingRef.current = true;
    setNodes((workflow?.nodes || []).map(toFlowNode));
    setEdges(workflow?.edges || []);
  }, [workflow]);

  useEffect(() => {
    if (!workflow) return;
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }
    onWorkflowChange({
      ...workflow,
      nodes: serializeNodes(nodes, workflow?.nodes || []),
      edges,
    });
  }, [nodes, edges]);

  const selectedNode = useMemo(() => workflow?.nodes?.find((node) => node.id === selectedNodeId) || null, [workflow, selectedNodeId]);

  const handleNodesChange = (changes) => {
    setNodes((prev) => applyNodeChanges(changes, prev));
  };

  const handleEdgesChange = (changes) => {
    setEdges((prev) => applyEdgeChanges(changes, prev));
  };

  const handleConnect = (connection) => {
    setEdges((prev) => addEdge({ ...connection, animated: false }, prev));
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[1.4fr_0.6fr]" data-testid="agent-os-workflow-builder">
      <div className="overflow-hidden rounded-[28px] border border-border-subtle bg-card/40" data-testid="agent-os-workflow-canvas-shell">
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Workflow builder</p>
            <h4 className="text-base font-semibold text-text-heading">{workflow?.name}</h4>
          </div>
          <p className="text-xs text-text-muted">{workflow?.nodes?.length || 0} nodos · {workflow?.edges?.length || 0} conexiones</p>
        </div>
        <div className="h-[560px] w-full" data-testid="agent-os-reactflow-canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={handleConnect}
            onNodeClick={(_, node) => onSelectNode(node.id)}
            nodeTypes={nodeTypes}
            fitView
          >
            <MiniMap pannable zoomable className="!bg-background/80" />
            <Controls className="!bg-background/90 !border !border-border-subtle !rounded-2xl !shadow-none" />
            <Background gap={24} size={1} color="rgba(255,255,255,0.08)" />
          </ReactFlow>
        </div>
      </div>

      <div className="rounded-[28px] border border-border-subtle bg-background/80 p-5 backdrop-blur-xl" data-testid="agent-os-node-config-panel">
        {selectedNode ? (
          <>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Configuración del nodo</p>
            <h4 className="mt-2 text-lg font-semibold text-text-heading">{selectedNode.label}</h4>
            <div className="mt-5 space-y-4 text-sm">
              <div>
                <p className="mb-1 text-xs uppercase tracking-[0.18em] text-text-muted">Tipo</p>
                <div className="rounded-2xl border border-border-subtle bg-card/50 px-3 py-2 text-text-body" data-testid="agent-os-selected-node-type">{selectedNode.type}</div>
              </div>
              <div>
                <p className="mb-1 text-xs uppercase tracking-[0.18em] text-text-muted">Descripción</p>
                <div className="rounded-2xl border border-border-subtle bg-card/50 px-3 py-3 text-text-body whitespace-pre-wrap" data-testid="agent-os-selected-node-description">{selectedNode.description || 'Sin descripción'}</div>
              </div>
              <div>
                <p className="mb-1 text-xs uppercase tracking-[0.18em] text-text-muted">Prompt / acción</p>
                <div className="rounded-2xl border border-border-subtle bg-card/50 px-3 py-3 text-text-body whitespace-pre-wrap" data-testid="agent-os-selected-node-prompt">{selectedNode.config?.prompt || selectedNode.config?.action || 'Sin instrucción específica'}</div>
              </div>
              <div>
                <p className="mb-1 text-xs uppercase tracking-[0.18em] text-text-muted">Tool / agente</p>
                <div className="rounded-2xl border border-border-subtle bg-card/50 px-3 py-2 text-text-body" data-testid="agent-os-selected-node-tool">{selectedNode.config?.tool_key || selectedNode.config?.agent_role || 'Interno'}</div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-full min-h-[240px] items-center justify-center rounded-[24px] border border-dashed border-border-subtle px-6 text-center text-sm text-text-muted" data-testid="agent-os-node-config-empty">
            Selecciona un nodo del canvas para revisar su configuración y editarlo desde el panel principal.
          </div>
        )}
      </div>
    </div>
  );
}